import { useCallback, useMemo } from 'react';
import { useSearch, useRouter } from '@tanstack/react-router';

/**
 * Bridge hook: same API as useQueryParams, but reads/writes through TanStack Router's
 * validated search params instead of raw window.location.search + history.replaceState.
 *
 * Benefits over useQueryParams:
 * - Params are validated by Zod schema at the route level (with defaults)
 * - Available from the very first render (no useLayoutEffect flash)
 * - Updates go through the router, so all subscribers update atomically
 *
 * Usage:
 *   const { param, setParam, deleteParam } = useRouteSearch('/_authenticated/integrations/', 'integration');
 */
export function useRouteSearch(from: string, key: string) {
  const search = useSearch({ strict: false }) as Record<string, string>;
  const router = useRouter();

  const param = useMemo(() => search[key] ?? '', [search, key]);

  const setParam = useCallback(
    (value: string | number) => {
      const currentSearch = router.state.location.search as Record<string, string>;
      void router.navigate({
        to: '.',
        search: { ...currentSearch, [key]: String(value) },
        replace: true,
      });
    },
    [router, key]
  );

  const deleteParam = useCallback(() => {
    const currentSearch = router.state.location.search as Record<string, string>;
    void router.navigate({
      to: '.',
      search: { ...currentSearch, [key]: '' },
      replace: true,
    });
  }, [router, key]);

  return { param, setParam, deleteParam };
}
